import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { TEAM_K9_LOGO_URL, FALLBACK_LOGO_URL } from '../../common/assets';

const AppLayout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const [isTrainerAuthenticated, setIsTrainerAuthenticated] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();

  // Check trainer authentication status
  React.useEffect(() => {
    const checkTrainerAuth = () => {
      const sessionCodeVerified = sessionStorage.getItem('trainerAccessVerified') === 'true';
      const sessionProfileId = sessionStorage.getItem('trainerProfileId');
      setIsTrainerAuthenticated(sessionCodeVerified && sessionProfileId);
    };

    checkTrainerAuth();

    // Listen for storage changes (in case auth status changes in another tab)
    const handleStorageChange = () => {
      checkTrainerAuth();
    };

    window.addEventListener('storage', handleStorageChange);

    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLogout = async () => {
    try {
      const { supabase } = await import('../../supabaseClient');
      await supabase.auth.signOut();
      sessionStorage.removeItem('trainerAccessVerified');
      sessionStorage.removeItem('trainerProfileId');
      setIsTrainerAuthenticated(false);
      navigate('/trainer-login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const NavItem = ({ to, icon, children, onClick }) => (
    <Link
      to={to}
      onClick={() => {
        onClick?.();
        setMobileMenuOpen(false);
      }}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
        location.pathname === to
          ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-700'
          : 'text-gray-700 hover:bg-gray-50'
      }`}
    >
      <SafeIcon name={icon} className="w-5 h-5 flex-shrink-0" />
      <span>{children}</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src={logoError ? FALLBACK_LOGO_URL : TEAM_K9_LOGO_URL}
                alt="Team K9 Logo" 
                className="w-12 h-12 sm:w-14 sm:h-14 object-contain"
                onError={() => setLogoError(true)}
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Team K9 Training</h1>
                <p className="text-xs text-gray-500 hidden sm:block">
                  {location.pathname.includes('/portal')
                    ? 'Owner Portal'
                    : location.pathname.includes('/trainer-') || location.pathname === '/dashboard'
                    ? 'Trainer Portal'
                    : 'Training Management'}
                </p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <NavItem to="/portal" icon="Users">
                Owner Portal
              </NavItem>
              <NavItem to="/trainer-login" icon="User">
                Trainer Login
              </NavItem>
              {isTrainerAuthenticated && (
                <NavItem to="/dashboard" icon="LayoutDashboard">
                  Trainer Dashboard
                </NavItem>
              )}
              {isTrainerAuthenticated && (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <SafeIcon name="LogOut" className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              <SafeIcon name={mobileMenuOpen ? "X" : "Menu"} className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <nav className="max-w-7xl mx-auto px-4 py-4 space-y-2">
              <NavItem to="/portal" icon="Users">
                Owner Portal
              </NavItem>
              <NavItem to="/trainer-login" icon="User">
                Trainer Login
              </NavItem>
              {isTrainerAuthenticated && (
                <NavItem to="/dashboard" icon="LayoutDashboard">
                  Trainer Dashboard
                </NavItem>
              )}
              {isTrainerAuthenticated && (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 w-full text-left text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <SafeIcon name="LogOut" className="w-5 h-5" />
                  <span>Sign Out</span>
                </button>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>
    </div>
  );
};

export default AppLayout;